import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  Badge,
  Button,
  ConfirmModal,
  Input,
  ProviderLogo,
  Table,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
  getStatusVariant,
} from '@/components/ui';
import { useToast } from '../../components/ui/Toast';
import { routes } from '../../lib/routes';
import { getProviderFallback } from '../../lib/provider-instructions';
import {
  useOrgIntegrations,
  useDeleteOrgIntegration,
} from '../../hooks/useOrgIntegrations';
import { useProviders } from '../../hooks/useIntegrations';
import type { Integration, Provider } from '../../types/integrations';
import { formatRelativeTime } from '../activity/utils';
import { ExternalLink } from 'lucide-react';

export default function OrgIntegrationList() {
  const navigate = useNavigate();
  const { success, error: showError } = useToast();
  const { data: providers, isLoading: providersLoading } = useProviders();
  const { data: integrations, isLoading: integrationsLoading } = useOrgIntegrations();
  const deleteIntegration = useDeleteOrgIntegration();

  const [searchQuery, setSearchQuery] = useState('');
  const [deleteModal, setDeleteModal] = useState<{
    isOpen: boolean;
    integration: Integration | null;
  }>({ isOpen: false, integration: null });

  // The backend may or may not honour `?scope=org` yet — if it returns mixed
  // scopes, filter client-side to keep this surface unambiguous.
  const orgIntegrations = (integrations ?? []).filter(
    (i) => i.scope === undefined || i.scope === 'org',
  );

  const filteredIntegrations = orgIntegrations.filter(
    (i) =>
      i.displayName.toLowerCase().includes(searchQuery.toLowerCase()) ||
      i.provider.toLowerCase().includes(searchQuery.toLowerCase()),
  );

  const providerByName = new Map<string, Provider>(
    (providers ?? []).map((p) => [p.name, p]),
  );
  const getProviderDisplayName = (name: string) =>
    providerByName.get(name)?.displayName ?? getProviderFallback(name).displayName;

  const handleDelete = async () => {
    if (!deleteModal.integration) return;
    try {
      await deleteIntegration.mutateAsync(deleteModal.integration.integrationId);
      success('Integration deleted');
      setDeleteModal({ isOpen: false, integration: null });
    } catch (err) {
      showError(err instanceof Error ? err.message : 'Failed to delete integration');
    }
  };

  const columns = [
    {
      key: 'displayName',
      header: 'Name',
      render: (item: Integration) => (
        <div className="flex items-center gap-3">
          <ProviderLogo
            slug={item.provider}
            displayName={getProviderDisplayName(item.provider)}
            className="h-8 w-8"
            grayscale
          />
          <div className="min-w-0">
            <p className="font-medium text-foreground">{item.displayName}</p>
            <p className="text-xs text-muted-foreground">
              {getProviderDisplayName(item.provider)}
            </p>
          </div>
        </div>
      ),
    },
    {
      key: 'status',
      header: 'Status',
      render: (item: Integration) => (
        <Badge variant={getStatusVariant(item.status)}>{item.status}</Badge>
      ),
    },
    {
      key: 'ruleCount',
      header: 'Rules',
      render: (item: Integration) => (
        <span className="text-muted-foreground">{item.ruleCount ?? 0}</span>
      ),
    },
    {
      key: 'routed',
      header: 'Routed / unrouted',
      render: (item: Integration) => {
        const routed = item.routedEventCount ?? 0;
        const unrouted = item.unroutedEventCount ?? 0;
        return (
          <Tooltip>
            <TooltipTrigger asChild>
              <span className="text-muted-foreground cursor-default">
                {routed} / {unrouted}
              </span>
            </TooltipTrigger>
            <TooltipContent className="text-xs max-w-xs">
              Events forwarded to a space vs. events that didn&rsquo;t match any
              rule. Unrouted is normal — only the rules you write decide what
              gets forwarded.
            </TooltipContent>
          </Tooltip>
        );
      },
    },
    {
      key: 'lastEventAt',
      header: 'Last event',
      render: (item: Integration) => {
        if (!item.lastEventAt) {
          return <span className="text-muted-foreground">Never</span>;
        }
        return (
          <Tooltip>
            <TooltipTrigger asChild>
              <span className="text-muted-foreground cursor-default">
                {formatRelativeTime(item.lastEventAt)}
              </span>
            </TooltipTrigger>
            <TooltipContent className="text-xs">
              {new Date(item.lastEventAt).toLocaleString()}
            </TooltipContent>
          </Tooltip>
        );
      },
    },
    {
      key: 'actions',
      header: '',
      render: (item: Integration) => (
        <div className="flex items-center gap-2">
          <Button
            variant="ghost"
            size="sm"
            onClick={(e) => {
              e.stopPropagation();
              setDeleteModal({ isOpen: true, integration: item });
            }}
            className="text-red-600 hover:text-red-700 hover:bg-red-50 dark:text-red-400 dark:hover:text-red-300 dark:hover:bg-red-900/30"
          >
            Delete
          </Button>
        </div>
      ),
      className: 'text-right',
    },
  ];

  return (
    <div className="space-y-8">
      <p className="text-sm text-muted-foreground">
        Connect external services once at the org level, then route their
        events into spaces via routing rules.
      </p>

      {/* Provider catalog */}
      <div className="space-y-4">
        <h2 className="text-lg font-semibold text-foreground">Available providers</h2>
        {providersLoading ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {[1, 2, 3].map((i) => (
              <div
                key={i}
                className="h-36 rounded-lg border border-border bg-card animate-pulse"
              />
            ))}
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {(providers ?? []).map((provider) => {
              const eventCount = provider.supportedEventTypes?.length ?? 0;
              return (
                <div
                  key={provider.name}
                  className="rounded-lg border border-border bg-card p-5 flex flex-col justify-between"
                >
                  <div>
                    <div className="flex items-start gap-3 mb-2">
                      <ProviderLogo
                        slug={provider.name}
                        displayName={provider.displayName}
                        grayscale
                      />
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <h3 className="font-semibold text-foreground">
                            {provider.displayName}
                          </h3>
                          <Badge variant="gray">{provider.ingestionType}</Badge>
                        </div>
                      </div>
                    </div>
                    <p className="text-sm text-muted-foreground">
                      Receive{' '}
                      {eventCount > 0
                        ? `${eventCount} event type${eventCount === 1 ? '' : 's'}`
                        : 'events'}{' '}
                      from {provider.displayName} via {provider.ingestionType}.
                    </p>
                    {provider.documentationUrl && (
                      <a
                        href={provider.documentationUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-1 text-xs text-primary-600 hover:text-primary-500 mt-2"
                        onClick={(e) => e.stopPropagation()}
                      >
                        Documentation <ExternalLink className="h-3 w-3" />
                      </a>
                    )}
                  </div>
                  <div className="mt-4">
                    <Link
                      to={`${routes.console.org.integrationNew}?provider=${provider.name}`}
                    >
                      <Button size="sm">Connect</Button>
                    </Link>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Integrations table */}
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-semibold text-foreground">
            Configured integrations
          </h2>
        </div>

        <div className="flex gap-4">
          <div className="flex-1 max-w-md">
            <Input
              placeholder="Search integrations..."
              value={searchQuery}
              onChange={(e: { target: { value: string } }) =>
                setSearchQuery(e.target.value)
              }
            />
          </div>
        </div>

        <Table
          columns={columns}
          data={filteredIntegrations}
          keyExtractor={(item) => item.integrationId}
          onRowClick={(item) =>
            navigate(routes.console.org.integration(item.integrationId))
          }
          emptyMessage="No organization integrations yet. Connect a provider above to get started."
          isLoading={integrationsLoading}
        />
      </div>

      <ConfirmModal
        isOpen={deleteModal.isOpen}
        onClose={() => setDeleteModal({ isOpen: false, integration: null })}
        onConfirm={handleDelete}
        title="Delete integration"
        message={`Delete "${deleteModal.integration?.displayName}"? Routing rules attached to this integration will also be removed. Incoming webhooks will stop being processed. This cannot be undone.`}
        confirmText="Delete"
        isLoading={deleteIntegration.isPending}
      />
    </div>
  );
}
